/**
 * „Gerät anlegen“ nach einem unbekannten Scan (ADR 0026) -- für jeden, nur mit
 * Netz.
 *
 * Zwei Pflichtfelder: Inventarnummer und Bezeichnung. Die Nummer steht schon
 * da, wenn der Code wie eine aussieht. Die Bezeichnung schlägt vor, was es im
 * Bestand gibt. Der Ort ist vorbelegt: der Behälter, in dem man gerade ist,
 * sonst das zuletzt benutzte Ziel, sonst „Ohne festen Platz“.
 *
 * Die Maske liegt über der Seite, die Seite bleibt, wo sie war. Ohne Verbindung
 * zum Server bietet sie an, den Code für später zu merken -- nie eine Sackgasse.
 */

import { useState } from "react";
import { INVENTARNUMMER_MUSTER, type Artikel } from "@thw/contracts";
import type { Ziel } from "@thw/core-domain";
import { useOnline } from "../api/online.js";
import { useBestand } from "../lokal/bestand.js";
import { bezeichnungen, geraetAnlegen, vorbelegterOrt } from "../lokal/geraetAnlegen.js";
import { merken } from "../lokal/nachzuerfassen.js";
import { artikelMitNummer } from "../lokal/neuesPackstueck.js";
import { zielName, type Gebucht } from "../lokal/verschieben.js";
import { Eingabe, Knopf, Ladeanzeige, Rueckmeldung } from "./bausteine.jsx";
import { Wohin } from "./Wohin.jsx";

export interface GeraetAnlegenEigenschaften {
  /** Der gelesene Code. */
  code: string;
  /** Die Schreibweise einer Inventarnummer, falls der Code danach aussieht. */
  vorschlag: string | null;
  /** Der Behälter, in dem man gerade ist -- als Ort vorbelegt. */
  ort_id: string | null;
  aufFertig: (artikel: Artikel, gebucht: Gebucht | null) => void;
  aufAbbruch: () => void;
  /** Ein vorhandenes Gerät mit dieser Nummer statt eines neuen. */
  aufVorhanden?: (artikel: Artikel) => void;
}

export function GeraetAnlegen({
  code,
  vorschlag,
  ort_id,
  aufFertig,
  aufAbbruch,
  aufVorhanden,
}: GeraetAnlegenEigenschaften) {
  const bestand = useBestand();
  const online = useOnline();
  const [nummer, setNummer] = useState(vorschlag ?? "");
  const [bezeichnung, setBezeichnung] = useState("");
  const [ziel, setZiel] = useState<Ziel | null | undefined>(undefined);
  const [waehlen, setWaehlen] = useState(false);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [gemerkt, setGemerkt] = useState(false);

  if (bestand === undefined) return <Ladeanzeige was="Der Bestand" />;
  // Einmal beim Öffnen vorbelegt, danach gilt, was gewählt wurde.
  if (ziel === undefined) {
    setZiel(vorbelegterOrt(bestand, ort_id));
    return null;
  }

  if (waehlen) {
    return (
      <Wohin
        titel="Wo liegt das Gerät?"
        aufAbbruch={() => {
          setWaehlen(false);
        }}
        aufWahl={(z) => {
          setWaehlen(false);
          setZiel(z);
        }}
      />
    );
  }

  const nr = nummer.trim();
  const nummerFalsch = nr !== "" && !INVENTARNUMMER_MUSTER.test(nr);
  const vorhanden = nr === "" || nummerFalsch ? null : artikelMitNummer(bestand, nr);
  const vorschlaege = bezeichnungen(bestand, bezeichnung);
  const kannAnlegen =
    online &&
    !laeuft &&
    nr !== "" &&
    !nummerFalsch &&
    vorhanden === null &&
    bezeichnung.trim() !== "";
  const zielText = ziel === null ? "nirgends" : zielName(bestand, ziel);

  const anlegen = async (): Promise<void> => {
    setLaeuft(true);
    setFehler(null);
    try {
      const { artikel, gebucht } = await geraetAnlegen({
        inventarnummer: nr,
        bezeichnung,
        code,
        ziel,
      });
      aufFertig(artikel, gebucht);
    } catch (f) {
      setFehler(f instanceof Error ? f.message : String(f));
      setLaeuft(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Gerät anlegen"
      className="fixed inset-0 z-30 overflow-y-auto bg-neutral-100 text-neutral-900 dark:bg-neutral-950 dark:text-neutral-50"
    >
      <header className="sticky top-0 z-10 flex items-center justify-between gap-2 bg-blue-800 px-4 py-2 text-white">
        <h2 className="text-lg font-bold">Gerät anlegen</h2>
        <button
          type="button"
          aria-label="Schließen"
          onClick={aufAbbruch}
          className="flex min-h-12 min-w-12 items-center justify-center rounded-lg text-2xl font-bold active:bg-blue-900"
        >
          ×
        </button>
      </header>

      <div className="mx-auto flex max-w-2xl flex-col gap-4 p-4 pb-24">
        {(!online || fehler !== null) && (
          <Rueckmeldung
            art="warnung"
            kopf={online ? "Nicht angelegt" : "Nur mit Netz"}
            zusatz={
              <div className="flex flex-col gap-2">
                <p>
                  {fehler ??
                    "Ein Gerät lässt sich nur mit Verbindung zum Server anlegen. Der Code kann für später gemerkt werden."}
                </p>
                {gemerkt ? (
                  <p className="font-semibold">✓ Unter „Nachzuerfassen“ gemerkt.</p>
                ) : (
                  <Knopf
                    onClick={() => {
                      void merken(code, vorschlag, ort_id).then(() => {
                        setGemerkt(true);
                      });
                    }}
                  >
                    Für später merken
                  </Knopf>
                )}
              </div>
            }
          />
        )}

        <p className="text-sm text-neutral-600 dark:text-neutral-300">
          Gelesen: <span className="font-mono">{code}</span>
        </p>

        <Eingabe
          beschriftung="Inventarnummer"
          wert={nummer}
          aendern={(neu) => {
            setNummer(neu.toUpperCase());
          }}
          hinweis={
            nummerFalsch
              ? "Keine THW-Inventarnummer (Muster 0141-900639)."
              : vorhanden !== null
                ? `Gibt es schon: ${vorhanden.bezeichnung}.`
                : nr === ""
                  ? "Vom Typenschild abtippen."
                  : undefined
          }
        />
        {vorhanden !== null && aufVorhanden !== undefined && (
          <Knopf
            onClick={() => {
              aufVorhanden(vorhanden);
            }}
          >
            {`${vorhanden.bezeichnung} nehmen`}
          </Knopf>
        )}

        <section className="flex flex-col gap-2">
          <Eingabe beschriftung="Bezeichnung" wert={bezeichnung} aendern={setBezeichnung} />
          {vorschlaege.length > 0 && (
            <div className="flex flex-wrap gap-2" aria-label="Vorschläge">
              {vorschlaege.map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => {
                    setBezeichnung(v);
                  }}
                  className="min-h-12 rounded-full bg-blue-100 px-4 font-semibold text-blue-950 active:bg-blue-200 dark:bg-blue-950 dark:text-blue-100"
                >
                  {v}
                </button>
              ))}
            </div>
          )}
        </section>

        <section className="flex flex-col gap-2">
          <span className="text-sm font-semibold">Wo liegt es?</span>
          <div className="flex items-center justify-between gap-2 rounded-xl bg-white p-3 dark:bg-neutral-800">
            <span className="font-semibold">{zielText}</span>
            <Knopf
              art="still"
              onClick={() => {
                setWaehlen(true);
              }}
            >
              Ändern
            </Knopf>
          </div>
        </section>

        <Knopf art="haupt" disabled={!kannAnlegen} onClick={() => void anlegen()}>
          {ziel === null ? "Anlegen" : `Anlegen und in ${zielText} einbuchen`}
        </Knopf>
        <p className="text-sm text-neutral-600 dark:text-neutral-300">
          Genauer wird es mit dem nächsten Import aus THWin. Die Datei gilt dann.
        </p>
      </div>
    </div>
  );
}
